import { useEffect, useRef, useState } from 'react';
import { Music, Pause, Play, Volume2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { KaraokeEngine, PRESET_LIST } from '../lib/karaokeEngine';

/**
 * KaraokeTracks — pick a genre and play a live procedural backing track
 * the user can sing over. 100% Web Audio, no asset files, no copyright.
 */
export default function KaraokeTracks() {
  const engineRef = useRef(null);
  const [active, setActive] = useState(null); // preset key
  const [volume, setVolume] = useState(0.5);

  useEffect(() => () => {
    engineRef.current?.dispose();
    engineRef.current = null;
  }, []);

  const ensureEngine = () => {
    if (!engineRef.current) {
      try { engineRef.current = new KaraokeEngine(); }
      catch (err) { console.warn('[Karaoke] engine init failed', err); }
    }
    return engineRef.current;
  };

  const togglePreset = (key) => {
    const eng = ensureEngine();
    if (!eng) return;
    if (active === key) {
      eng.stop();
      setActive(null);
    } else {
      eng.setVolume(volume);
      eng.start(key);
      setActive(key);
    }
  };

  const onVolume = (v) => {
    setVolume(v);
    engineRef.current?.setVolume(v);
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid="karaoke-tracks">
      <div className="flex items-center gap-2 mb-3">
        <Music size={18} className="text-cyan-400" />
        <h3 className="font-bold text-white">Pistes karaoké</h3>
        <Badge className="bg-cyan-500/15 text-cyan-300 border-0 text-[10px] ml-auto">100% libre</Badge>
      </div>
      <p className="text-xs text-zinc-400 mb-3">Choisis un beat et chante par-dessus. Volume séparé du micro.</p>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {PRESET_LIST.map((p) => {
          const isActive = active === p.key;
          return (
            <button
              key={p.key}
              onClick={() => togglePreset(p.key)}
              data-testid={`karaoke-preset-${p.key}`}
              className={`relative aspect-square rounded-2xl p-2 flex flex-col items-center justify-center gap-1 transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-cyan-500 to-pink-500 shadow-lg shadow-cyan-500/40 scale-105'
                  : 'bg-white/5 hover:bg-white/10 border border-white/10'
              }`}
            >
              <div className="text-2xl">{p.emoji}</div>
              <div className="text-xs font-bold text-white">{p.label}</div>
              <div className="text-[9px] text-zinc-300/80 font-mono">{p.bpm} BPM</div>
              {isActive && (
                <div className="absolute top-1.5 right-1.5">
                  <Pause size={10} className="text-white" />
                </div>
              )}
              {!isActive && (
                <Play size={10} className="absolute top-1.5 right-1.5 text-zinc-300" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Volume2 size={14} className="text-zinc-400" />
        <input
          type="range"
          min={0} max={1} step={0.05}
          value={volume}
          onChange={(e) => onVolume(Number(e.target.value))}
          className="flex-1 accent-pink-500"
          data-testid="karaoke-volume"
        />
        <span className="text-xs text-zinc-400 w-8 text-right tabular-nums">{Math.round(volume * 100)}%</span>
      </div>

      {active && (
        <button
          onClick={() => togglePreset(active)}
          className="w-full mt-3 h-9 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs font-bold transition-colors"
          data-testid="karaoke-stop"
        >
          ⏹ Arrêter la piste
        </button>
      )}
    </div>
  );
}
